import { _splitPath } from '../objects/_split_path';
import { is } from '../util/is';

const emptyArrayCommand = '_@_';
const splitEmptyArray = /\[\]/g;

type t_set_object = Record<string, unknown>;

/**
 * Takes an object, a path and a value and mutates the object such that the value exists at the path.
 *
 * ```ts
 * const obj = {
 *   prop1: {
 *     prop2: {
 *       prop3: '3'
 *     }
 *   }
 * };
 * tt.set('prop1.prop2.prop3', obj, '1')
 * // obj === { prop1: { prop2: { prop3: '1'} } };
 * ```
 *
 * @param path A string representing a path in an object, e.g. `prop1[0].prop2`.
 * @param object The object on which to set a property.
 * @param value The value to set at the given `path`.
 */
export function set<T>(path: string, object: t_set_object, value: T) {
  if (path === '') {
    return object;
  }

  const pathWithKey = path.replace(splitEmptyArray, `[${emptyArrayCommand}]`);

  _set(_splitPath(pathWithKey), object, value);
  return object;
}

function _set<T>(pathSplit: string[], object: t_set_object, value: T): void {
  let next = pathSplit.pop();

  if (next === emptyArrayCommand) {
    next = (Array.from((object as unknown) as Iterable<unknown>) as Array<unknown>).length + '';
  }

  if (pathSplit.length === 0) {
    object[next as string] = value;
    return;
  }

  if (is(undefined, object[next as string])) {
    const lookAhead = pathSplit[pathSplit.length - 1];
    const keyOrIndex = parseInt(lookAhead, 10);

    object[next as string] = lookAhead === emptyArrayCommand || is(Number, keyOrIndex) ? [] : {};
  }

  return _set(pathSplit, object[next as string] as Record<string, unknown>, value);
}
